import { useEffect, useState, type RefObject } from 'react';
import { track } from '../lib/amplitude';
import { timecode } from '../lib/format';
import { Spinner } from '../components/ui';

/**
 * The words, under the video, each line a link to its moment.
 *
 * Built from the same WebVTT the player uses for captions, so the two can
 * never disagree. Cues are merged into lines of a few seconds each: the
 * caption file breaks at every breath, which is right on screen and
 * unreadable as prose.
 */
interface Cue {
  start: number;
  end: number;
  text: string;
}

function parseVtt(source: string): Cue[] {
  const cues: Cue[] = [];
  const blocks = source.replace(/\r/g, '').split(/\n\n+/);
  for (const block of blocks) {
    const lines = block.split('\n').filter(Boolean);
    const timing = lines.find((line) => line.includes('-->'));
    if (!timing) continue;
    const [from, to] = timing.split('-->').map((part) => part.trim().split(' ')[0]);
    const text = lines
      .slice(lines.indexOf(timing) + 1)
      .join(' ')
      .replace(/<[^>]+>/g, '')
      .trim();
    if (!text) continue;
    cues.push({ start: seconds(from), end: seconds(to), text });
  }
  return cues;
}

function seconds(stamp: string): number {
  const parts = stamp.split(':').map(Number);
  if (parts.some(Number.isNaN)) return 0;
  return parts.reduce((total, part) => total * 60 + part, 0);
}

/** Cues folded into lines of roughly `span` seconds. */
function fold(cues: Cue[], span = 12): Cue[] {
  const lines: Cue[] = [];
  for (const cue of cues) {
    const last = lines[lines.length - 1];
    // Join while the line is short, the previous cue did not end a
    // sentence, and there was no real pause between them.
    if (
      last &&
      cue.end - last.start <= span &&
      !/[.?!]$/.test(last.text) &&
      cue.start - last.end < 1.2
    ) {
      last.text = `${last.text} ${cue.text}`;
      last.end = cue.end;
    } else {
      lines.push({ ...cue });
    }
  }
  return lines;
}

export function Transcript({
  status,
  captionsUrl,
  mediaRef,
  title,
  shareId,
  fill = false,
}: {
  status: 'pending' | 'ready' | 'failed';
  captionsUrl: string | null;
  mediaRef: RefObject<HTMLMediaElement | null>;
  /** The recording's title, for the downloaded file. */
  title: string;
  /** The share id, for the download event. Omitted outside a share page. */
  shareId?: string;
  /** Inside a panel that scrolls on its own: no inner scroll region. */
  fill?: boolean;
}) {
  const [lines, setLines] = useState<Cue[] | null>(null);
  const [failed, setFailed] = useState(false);
  const [current, setCurrent] = useState(0);

  useEffect(() => {
    if (!captionsUrl) return;
    let cancelled = false;
    fetch(captionsUrl)
      .then((response) =>
        response.ok ? response.text() : Promise.reject(new Error(String(response.status))),
      )
      .then((text) => {
        if (!cancelled) setLines(fold(parseVtt(text)));
      })
      .catch(() => {
        if (!cancelled) setFailed(true);
      });
    return () => {
      cancelled = true;
    };
  }, [captionsUrl]);

  // Follow the playhead so the line being spoken is the one that is lit.
  useEffect(() => {
    const media = mediaRef.current;
    if (!media) return;
    const update = () => setCurrent(media.currentTime);
    media.addEventListener('timeupdate', update);
    media.addEventListener('seeked', update);
    return () => {
      media.removeEventListener('timeupdate', update);
      media.removeEventListener('seeked', update);
    };
  }, [mediaRef, lines]);

  const seek = (at: number) => {
    const media = mediaRef.current;
    if (!media) return;
    media.currentTime = at;
    void media.play().catch(() => {});
  };

  if (status === 'failed' || failed) {
    return (
      <section>
        <h2 className="text-h4 text-chalk">Transcript</h2>
        <p className="mt-2 text-body text-chalk-dim">
          No transcript for this one. There may have been no speech to hear.
        </p>
      </section>
    );
  }

  if (status === 'pending' || lines === null) {
    return (
      <section>
        <h2 className="text-h4 text-chalk">Transcript</h2>
        <p className="mt-2 flex items-center gap-2 text-body text-chalk-dim">
          <Spinner size={14} />
          Listening. Captions and the transcript appear here in a minute or two.
        </p>
      </section>
    );
  }

  if (lines.length === 0) {
    return (
      <section>
        <h2 className="text-h4 text-chalk">Transcript</h2>
        <p className="mt-2 text-body text-chalk-dim">Nothing was said.</p>
      </section>
    );
  }

  // A text file, timecoded line by line, named after the recording. Built
  // here rather than linking the VTT: the captions file is broken at every
  // breath and full of cue numbers, which is right for a player and wrong
  // for a document.
  const download = () => {
    const body = lines.map((line) => `[${timecode(line.start)}] ${line.text}`).join('\n');
    const blob = new Blob([`${title}\n\n${body}\n`], { type: 'text/plain;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = `${title.replace(/[\\/:*?"<>|]+/g, ' ').trim() || 'transcript'}.txt`;
    anchor.click();
    URL.revokeObjectURL(url);
    track('Transcript Downloaded', shareId ? { video_id: shareId } : {});
  };

  return (
    <section>
      <div className="flex items-center justify-between gap-3">
        <h2 className="text-h4 text-chalk">Transcript</h2>
        <button
          type="button"
          className="text-caption text-chalk-faint hover:text-chalk"
          onClick={download}
        >
          Download transcript
        </button>
      </div>
      <ol className={fill ? 'mt-3 space-y-1' : 'mt-3 max-h-96 space-y-1 overflow-y-auto pr-2'}>
        {lines.map((line) => {
          const live = current >= line.start && current < line.end;
          return (
            <li key={line.start}>
              <button
                type="button"
                onClick={() => seek(line.start)}
                className={`flex w-full gap-3 rounded-md px-2 py-1.5 text-left transition-colors hover:bg-slate-900 ${live ? 'bg-slate-900' : ''}`}
              >
                <span className="w-12 shrink-0 font-mono text-caption text-signal">
                  {timecode(line.start)}
                </span>
                <span className={`text-body ${live ? 'text-chalk' : 'text-chalk-dim'}`}>
                  {line.text}
                </span>
              </button>
            </li>
          );
        })}
      </ol>
    </section>
  );
}
